import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { dashboardApi, projectsApi } from '../lib/api';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  FolderKanban,
  CheckSquare,
  Calendar,
  Receipt,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowRight,
  Clock,
  ClipboardList,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color, testId }) => (
  <Card className="bg-[#141414] border-white/5 stat-card" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-zinc-400 mb-1">{title}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              {trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="text-white" size={24} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { selectedClientId, hasPermission } = useClient();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const canSeeFinances = user?.role === 'admin' || (user?.position || '').toLowerCase().includes('contad');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const clientId = selectedClientId !== 'all' ? selectedClientId : undefined;
        const [statsRes, projectsRes] = await Promise.all([
          dashboardApi.getStats(clientId),
          projectsApi.getAll(clientId),
        ]);
        setStats(statsRes.data);
        setProjects(projectsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedClientId]);

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
  };

  const isClient = user?.role === 'client';

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8" data-testid="dashboard-content">
        {/* Stats Grid */}
        <div className={cn('grid gap-6 stagger-children', isClient ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4')}>
          {!isClient && <StatCard title="Tareas" value={`${stats?.tasks?.completed || 0}/${stats?.tasks?.total || 0}`} icon={CheckSquare} color="bg-green-600" testId="stat-tareas" />}
          <StatCard title="Contenido Planeado" value={stats?.content?.total || 0} icon={Calendar} trend={stats?.content?.pending_approval > 0 ? 'up' : null} trendValue={`${stats?.content?.pending_approval} pendientes`} color="bg-pink-600" testId="stat-contenido" />
          {isClient && <StatCard title="Ordenes" value={stats?.work_orders?.pending || 0} icon={ClipboardList} trendValue="pendientes" color="bg-orange-500" testId="stat-ordenes" />}
          {isClient && <StatCard title="Contenido Aprobado" value={stats?.content?.approved || 0} icon={CheckSquare} color="bg-green-600" testId="stat-aprobado" />}
          {!isClient && canSeeFinances ? (
            <StatCard title="Ingresos" value={formatCurrency(stats?.finances?.income)} icon={DollarSign} trend="up" trendValue={formatCurrency(stats?.finances?.profit)} color="bg-amber-600" testId="stat-ingresos" />
          ) : !isClient ? (
            <StatCard title="Ordenes" value={stats?.work_orders?.pending || 0} icon={ClipboardList} trendValue="pendientes" color="bg-amber-600" testId="stat-ordenes" />
          ) : null}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upcoming Content for everyone */}
          <Card className="lg:col-span-2 bg-[#141414] border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Contenido Proximo</CardTitle>
              <Link to="/content"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">Ver calendario <ArrowRight className="ml-2" size={16} /></Button></Link>
            </CardHeader>
            <CardContent>
              {!stats?.upcoming_content?.length ? (
                <p className="text-zinc-400 text-center py-4">Sin contenido programado</p>
              ) : (
                <div className="space-y-3">
                  {stats.upcoming_content.map((content) => (
                    <div key={content.content_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A]">
                      <Clock className="text-zinc-500" size={16} /><span className="flex-1 text-white truncate">{content.title}</span>
                      <span className="text-sm text-zinc-400">{content.scheduled_date && format(new Date(content.scheduled_date), 'd MMM', { locale: es })}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Finance or Activity panel */}
          {canSeeFinances && !isClient ? (
            <Card className="bg-[#141414] border-white/5">
              <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-white">Flujo de Caja</CardTitle><Link to="/finances"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">Detalles <ArrowRight className="ml-2" size={16} /></Button></Link></CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20"><div className="flex items-center gap-2 text-green-400 mb-2"><TrendingUp size={20} /><span className="text-sm font-medium">Ingresos</span></div><p className="text-2xl font-bold text-white">{formatCurrency(stats?.finances?.income)}</p></div>
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20"><div className="flex items-center gap-2 text-red-400 mb-2"><TrendingDown size={20} /><span className="text-sm font-medium">Gastos</span></div><p className="text-2xl font-bold text-white">{formatCurrency(stats?.finances?.expenses)}</p></div>
                  <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20"><div className="flex items-center gap-2 text-orange-400 mb-2"><DollarSign size={20} /><span className="text-sm font-medium">Ganancia Neta</span></div><p className="text-2xl font-bold text-white">{formatCurrency(stats?.finances?.profit)}</p></div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#141414] border-white/5">
              <CardHeader><CardTitle className="text-white">{isClient ? 'Mis Ordenes Recientes' : 'Actividad Reciente'}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(isClient ? [] : stats?.recent_tasks?.slice(0, 3))?.map((task) => (
                    <div key={task.task_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A]">
                      <div className={`w-2 h-2 rounded-full ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-orange-500' : 'bg-green-500'}`} />
                      <span className="flex-1 text-white truncate text-sm">{task.title}</span>
                    </div>
                  ))}
                  {isClient && <p className="text-center text-zinc-400 py-4"><Link to="/work-orders" className="text-orange-400 hover:text-orange-300">Ver todas las ordenes</Link></p>}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tasks & Content (non-client only) */}
        {!isClient && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-[#141414] border-white/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Tareas Recientes</CardTitle>
                <Link to="/tasks"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">Ver todas <ArrowRight className="ml-2" size={16} /></Button></Link>
              </CardHeader>
              <CardContent>
                {stats?.recent_tasks?.length === 0 ? (
                  <p className="text-zinc-400 text-center py-4">Sin tareas recientes</p>
                ) : (
                  <div className="space-y-3">
                    {stats?.recent_tasks?.map((task) => (
                      <div key={task.task_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A]">
                        <div className={`w-2 h-2 rounded-full ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-orange-500' : 'bg-green-500'}`} />
                        <span className="flex-1 text-white truncate">{task.title}</span>
                        <span className={`px-2 py-1 rounded text-xs ${task.status === 'done' ? 'bg-green-500/20 text-green-400' : task.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-500/20 text-zinc-400'}`}>{task.status === 'done' ? 'Hecho' : task.status === 'in_progress' ? 'En progreso' : 'Por hacer'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#141414] border-white/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">Contenido Proximo</CardTitle>
                <Link to="/content"><Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">Ver todo <ArrowRight className="ml-2" size={16} /></Button></Link>
              </CardHeader>
              <CardContent>
                {stats?.upcoming_content?.length === 0 ? (
                  <p className="text-zinc-400 text-center py-4">Sin contenido programado</p>
                ) : (
                  <div className="space-y-3">
                    {stats?.upcoming_content?.map((content) => (
                      <div key={content.content_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A]">
                        <Clock className="text-zinc-500" size={16} /><span className="flex-1 text-white truncate">{content.title}</span>
                        <span className="text-sm text-zinc-400">{content.scheduled_date && format(new Date(content.scheduled_date), 'd MMM', { locale: es })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
